import React, { useEffect, useState, useRef, useMemo, useCallback, memo } from 'react';
import API from '../api';
import { toast } from 'react-toastify';
import { useDebounce } from '../hooks/useDebounce';
import { usePharmacy } from '../context/PharmacyContext';

// --- Utility Functions (Outside Render) ---
const fmt = (num) =>
  Number.isFinite(Number(num))
    ? Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';

// --- Memoized Sub-Components ---
const TypeBadge = memo(({ type, isMobile, baseStyle, purchaseStyle }) => {
  const badgeStyles = {
    purchase: { background: '#fef5f5', color: '#991b1b' },
    payment: { background: '#f0fdf4', color: '#166534' },
    sale: { background: '#eff6ff', color: '#1e40af' },
    receipt: { background: '#ecfdf5', color: '#065f46' },
    return: { background: '#fef5f1', color: '#9a3412' }
  };
  const labels = {
    purchase: isMobile ? 'Purchase' : 'Procurement Pur.',
    payment: isMobile ? 'Payment' : 'Vendor Payment',
    sale: 'Sale',
    receipt: 'Receipt',
    return: 'Return'
  };
  return (
    <span style={{ ...baseStyle, ...(badgeStyles[type] || purchaseStyle) }}>
      {labels[type] || type}
    </span>
  );
});

const AmountDisplay = memo(({ value, type, isMobile }) => {
  const color = (type === 'purchase' || type === 'sale') ? '#dc2626' : (type === 'payment' || type === 'receipt') ? '#16a34a' : '#c05621';
  return <span style={{ fontWeight: 600, color, fontSize: isMobile ? 13 : 14 }}>Rs {fmt(value)}</span>;
});

const MobileTxCard = memo(({ tx, isMobile, styles, onSelect }) => (
  <div
    style={styles.mobileCard}
    onClick={() => onSelect(tx)}
    onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'}
    onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1a202c' }}>{tx.party?.name || 'Unknown'}</div>
        <div style={{ fontSize: 11, color: '#718096', marginTop: 2 }}>{tx.invoiceNumber || 'No Invoice'}</div>
      </div>
      <AmountDisplay value={tx.amount} type={tx.type} isMobile={isMobile} />
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <TypeBadge type={tx.type} isMobile={isMobile} baseStyle={styles.badge} purchaseStyle={styles.badgePurchase} />
      <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>
        {new Date(tx.date).toLocaleDateString('en-IN')}
      </span>
    </div>
  </div>
));

export default function TransactionsPage() {
  // 1. Check for cached data for instant load
  const cachedTxs = sessionStorage.getItem('transactionsList');
  const initialTxs = cachedTxs ? JSON.parse(cachedTxs) : [];
  
  const [txs, setTxs] = useState(initialTxs);
  const [loading, setLoading] = useState(!cachedTxs); // Only show spinner if no cache
  
  const [parties, setParties] = useState([]);
  const [party, setParty] = useState('');
  const [type, setType] = useState('purchase');
  const [amount, setAmount] = useState('');
  const [invoice, setInvoice] = useState('');
  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q, 250);
  const [showAddForm, setShowAddForm] = useState(false);

  // Capital management
  const initialCapital = Number(localStorage.getItem('businessCapital')) || 0;
  const [capital, setCapital] = useState(initialCapital);
  const [showCapitalModal, setShowCapitalModal] = useState(false);
  const [newCapital, setNewCapital] = useState('');
  const [capitalPassword, setCapitalPassword] = useState('');
  const [capitalHistory, setCapitalHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const CAPITAL_PASSWORD = 'admin123';

  // Filtering state
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('ALL');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Pre-calculate initial summary to avoid showing 0s on instant load
  const initialTotalPurchases = initialTxs.filter(t => t.type === 'purchase').reduce((sum, t) => sum + t.amount, 0);
  const initialTotalPayments = initialTxs.filter(t => t.type === 'payment').reduce((sum, t) => sum + t.amount, 0);
  const initialTotalReturns = initialTxs.filter(t => t.type === 'return').reduce((sum, t) => sum + t.amount, 0);
  const initialPayable = initialTotalPurchases - initialTotalPayments - initialTotalReturns;

  const [summary, setSummary] = useState({
    totalCapital: initialCapital,
    totalPurchases: initialTotalPurchases,
    totalPayments: initialTotalPayments,
    totalReturns: initialTotalReturns,
    payable: initialPayable,
    remainingCapital: initialCapital - initialPayable
  });

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [editParty, setEditParty] = useState('');
  const [editType, setEditType] = useState('purchase');
  const [editAmount, setEditAmount] = useState('');
  const [editInvoice, setEditInvoice] = useState('');

  // Transaction detail modal (for mobile)
  const [selectedTx, setSelectedTx] = useState(null);

  // Enhanced Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  const printRef = useRef();

  // Responsive listener
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
      setIsSmallMobile(width <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load capital from localStorage
  useEffect(() => {
    const savedCapital = localStorage.getItem('businessCapital');
    const savedHistory = localStorage.getItem('capitalHistory');

    if (savedCapital) {
      setCapital(Number(savedCapital));
    } else {
      setCapital(0);
    }

    if (savedHistory) {
      try {
        setCapitalHistory(JSON.parse(savedHistory));
      } catch (e) {
        setCapitalHistory([]);
      }
    }
  }, []);

  // Calculate financial summary
  const calculateSummary = useCallback((transactions) => {
    const totalPurchases = transactions
      .filter(t => t.type === 'purchase')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalPayments = transactions
      .filter(t => t.type === 'payment')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalReturns = transactions
      .filter(t => t.type === 'return')
      .reduce((sum, t) => sum + t.amount, 0);

    const payable = totalPurchases - totalPayments - totalReturns;
    const remainingCapital = (capital || 0) - payable;

    setSummary({
      totalCapital: capital || 0,
      totalPurchases,
      totalPayments,
      totalReturns,
      payable,
      remainingCapital
    });
  }, [capital]);

  const fetchData = useCallback(async () => {
    // Only show spinner if we have absolutely no data (allows silent updates)
    if (txs.length === 0 && !sessionStorage.getItem('transactionsList')) {
      setLoading(true);
    }
    
    try {
      const { data } = await API.get('/transactions', { params: { q: debouncedQ } });
      setTxs(data);
      calculateSummary(data);
      
      // 2. Cache data if no search query is active
      if (!debouncedQ) {
        sessionStorage.setItem('transactionsList', JSON.stringify(data));
      }
    } catch (e) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, calculateSummary, txs.length]);

  const fetchParties = useCallback(async () => {
    try {
      const { data } = await API.get('/parties');
      setParties(data);
    } catch (e) {
      toast.error('Failed to fetch parties');
    }
  }, []);

  const { pharmacyDetails } = usePharmacy();
  const currentUserRole = pharmacyDetails?.role || 'admin';

  const filteredTxs = useMemo(() => {
    return txs.filter(t => {
      if (typeFilter !== 'ALL' && t.type !== typeFilter) return false;

      if (dateFilter !== 'ALL') {
        const txDate = new Date(t.date);
        const now = new Date();
        if (dateFilter === 'TODAY') {
          const todayStr = now.toISOString().split('T')[0];
          const txStr = txDate.toISOString().split('T')[0];
          if (todayStr !== txStr) return false;
        } else if (dateFilter === 'THIS_WEEK') {
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          if (txDate < weekAgo) return false;
        } else if (dateFilter === 'THIS_MONTH') {
          if (txDate.getMonth() !== now.getMonth() || txDate.getFullYear() !== now.getFullYear()) {
            return false;
          }
        } else if (dateFilter === 'CUSTOM') {
          if (customStartDate && txDate < new Date(customStartDate)) return false;
          if (customEndDate && txDate > new Date(customEndDate + 'T23:59:59')) return false;
        }
      }
      return true;
    });
  }, [txs, typeFilter, dateFilter, customStartDate, customEndDate]);

  useEffect(() => {
    fetchData();
    fetchParties();
  }, [debouncedQ, capital, fetchData, fetchParties]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    try {
      await API.post('/transactions', {
        party,
        type,
        amount: Number(amount),
        invoiceNumber: invoice,
      });
      setAmount('');
      setInvoice('');
      setParty('');
      setShowAddForm(false);
      fetchData();
      toast.success('Transaction added successfully');
    } catch (e) {
      toast.error('Error adding transaction');
    }
  }, [party, type, amount, invoice, fetchData]);

  const deleteTx = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    try {
      await API.delete(`/transactions/${id}`);
      setSelectedTx(null);
      fetchData();
      toast.success('Transaction deleted');
    } catch (e) {
      toast.error('Error deleting transaction');
    }
  }, [fetchData]);

  const openEdit = useCallback((tx) => {
    setEditTx(tx);
    setEditParty(tx.party?._id || '');
    setEditType(tx.type);
    setEditAmount(tx.amount);
    setEditInvoice(tx.invoiceNumber || '');
    setEditOpen(true);
    setSelectedTx(null);
  }, []);

  const updateTx = useCallback(async (e) => {
    e.preventDefault();
    try {
      await API.put(`/transactions/${editTx._id}`, {
        party: editParty,
        type: editType,
        amount: Number(editAmount),
        invoiceNumber: editInvoice,
      });
      setEditOpen(false);
      fetchData();
      toast.success('Transaction updated');
    } catch (e) {
      toast.error('Error updating transaction');
    }
  }, [editTx, editParty, editType, editAmount, editInvoice, fetchData]);

  const openCapitalModal = useCallback(() => {
    if (currentUserRole !== 'admin' && currentUserRole !== 'superadmin') {
      toast.error('Only Admin users can modify business capital balance.');
      return;
    }
    setNewCapital(capital !== null ? capital.toString() : '');
    setCapitalPassword('');
    setShowCapitalModal(true);
  }, [currentUserRole, capital]);

  const updateCapital = useCallback((e) => {
    e.preventDefault();

    if (capitalPassword !== CAPITAL_PASSWORD) {
      toast.error('Incorrect password');
      return;
    }

    const capitalValue = Number(newCapital);
    if (isNaN(capitalValue) || capitalValue < 0) {
      toast.warning('Enter a valid capital amount');
      return;
    }

    const historyEntry = {
      id: Date.now(),
      oldAmount: capital || 0,
      newAmount: capitalValue,
      change: capitalValue - (capital || 0),
      date: new Date().toISOString(),
      reason: capitalValue > (capital || 0) ? 'Capital Increase' : 'Capital Decrease'
    };

    const updatedHistory = [historyEntry, ...capitalHistory];
    setCapitalHistory(updatedHistory);

    setCapital(capitalValue);
    localStorage.setItem('businessCapital', capitalValue.toString());
    localStorage.setItem('capitalHistory', JSON.stringify(updatedHistory));

    setShowCapitalModal(false);
    setCapitalPassword('');
    toast.success('Capital updated successfully');
  }, [capitalPassword, newCapital, capital, capitalHistory]);

  const printContent = useCallback(() => {
    if (!printRef?.current) {
      toast.info('Nothing to print');
      return;
    }
    try {
      const content = printRef.current.innerHTML;
      const printWindow = window.open('', '', 'width=1000,height=800');
      printWindow.document.write(`
        <html>
          <head>
            <title>Transactions Export</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; color: #111; }
              table { width: 100%; border-collapse: collapse; margin-top: 14px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background: #f7fafc; }
              .text-right { text-align: right; }
              @media print {
                .no-print { display: none !important; }
              }
            </style>
          </head>
          <body>
            <h2>Transactions Report</h2>
            ${content}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } catch {
      toast.error('Print failed');
    }
  }, []);

  const txStats = useMemo(() => {
    return {
      total: txs.length,
      purchases: txs.filter(t => t.type === 'purchase').length,
      payments: txs.filter(t => t.type === 'payment').length,
      returns: txs.filter(t => t.type === 'return').length
    };
  }, [txs]);

  // ---------- Memoized Styles ----------
  const styles = useMemo(() => ({
    container: { minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Inter', sans-serif" },
    header: {
      background: '#ffffff', borderBottom: '1px solid #e1e8ed',
      padding: isMobile ? '12px 16px' : isTablet ? '16px 24px' : '20px 32px',
      display: 'flex', flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? 12 : 0, position: 'sticky', top: 0, zIndex: 100,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
    },
    headerTitle: { display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 },
    headerIcon: {
      width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, background: '#1880c2', 
      borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'white', fontSize: isMobile ? 10 : 12, fontWeight: 700, letterSpacing: '0.5px'
    },
    headerText: { fontSize: isMobile ? 16 : 20, fontWeight: 700, color: '#1a202c', letterSpacing: '-0.3px' },
    headerSubtext: { fontSize: isMobile ? 11 : 12, color: '#718096', fontWeight: 500, marginTop: 2 },
    headerActions: {
      display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10,
      width: isMobile ? '100%' : 'auto', flexWrap: 'wrap',
      flexDirection: isSmallMobile ? 'column' : 'row'
    },
    main: { maxWidth: 1400, margin: '0 auto', padding: isMobile ? '12px' : '24px 32px' },
    statsRow: {
      display: 'grid',
      gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
      gap: isMobile ? 10 : 16, marginBottom: isMobile ? 16 : 24
    },
    statCard: {
      background: '#ffffff', borderRadius: isMobile ? 8 : 10, padding: isMobile ? 12 : 20,
      border: '1px solid #e1e8ed', display: 'flex', gap: isMobile ? 10 : 12,
      alignItems: 'center', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)', position: 'relative'
    },
    statIconBox: {
      width: isMobile ? 36 : 48, height: isMobile ? 36 : 48, borderRadius: 8,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: isMobile ? 10 : 12, flexShrink: 0, fontWeight: 700, letterSpacing: '0.5px'
    },
    statContent: { flex: 1, minWidth: 0 },
    statNumber: { fontSize: isMobile ? 14 : 20, fontWeight: 800, color: '#1a202c' },
    statTitle: { fontSize: isMobile ? 9 : 10, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 },
    card: {
      background: '#ffffff', borderRadius: isMobile ? 8 : 10, border: '1px solid #e1e8ed',
      marginBottom: isMobile ? 16 : 24, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
    },
    cardHeader: {
      padding: isMobile ? '12px 16px' : '16px 24px', borderBottom: '1px solid #e1e8ed',
      display: 'flex', flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? 12 : 12, background: '#fafbfc'
    },
    cardTitle: { fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#1a202c', display: 'flex', alignItems: 'center', gap: 10, margin: 0 },
    formGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 12 : 16, alignItems: 'end' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: isMobile ? 10 : 11, fontWeight: 600, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: {
      padding: isMobile ? '8px 12px' : '10px 14px', borderRadius: 6, border: '1px solid #e1e8ed',
      fontSize: isMobile ? 13 : 14, width: '100%', boxSizing: 'border-box', background: '#fff',
      transition: 'border-color 0.2s', outline: 'none'
    },
    select: {
      padding: isMobile ? '8px 12px' : '10px 14px', borderRadius: 6, border: '1px solid #e1e8ed',
      fontSize: isMobile ? 13 : 14, width: '100%', boxSizing: 'border-box', background: '#fff',
      cursor: 'pointer', outline: 'none'
    },
    btnPrimary: {
      padding: isMobile ? '8px 14px' : '10px 20px', background: '#1880c2', color: 'white',
      border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
      fontSize: isMobile ? 13 : 14, transition: 'all 0.2s', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', gap: 8,
      flex: isSmallMobile ? 1 : 'none', width: isSmallMobile ? '100%' : 'auto'
    },
    btnSecondary: {
      padding: isMobile ? '8px 14px' : '10px 20px', background: '#ffffff', color: '#4a5568',
      border: '1px solid #e1e8ed', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
      fontSize: isMobile ? 13 : 14, transition: 'all 0.2s',
      flex: isSmallMobile ? 1 : 'none', width: isSmallMobile ? '100%' : 'auto'
    },
    btnSuccess: {
      padding: isMobile ? '8px 14px' : '10px 20px', background: '#16a34a', color: 'white',
      border: 'none', borderRadius: 6, fontSize: isMobile ? 13 : 14, fontWeight: 600,
      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, transition: 'all 0.2s', flex: isSmallMobile ? 1 : 'none', width: isSmallMobile ? '100%' : 'auto'
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      padding: isMobile ? '8px 10px' : '12px 16px', textAlign: 'left',
      fontSize: isMobile ? 10 : 11, fontWeight: 600, color: '#718096',
      background: '#fafbfc', borderBottom: '1px solid #e1e8ed',
      textTransform: 'uppercase', letterSpacing: '0.5px'
    },
    td: {
      padding: isMobile ? '10px' : '12px 16px', fontSize: isMobile ? 12 : 14,
      color: '#1a202c', borderBottom: '1px solid #f7fafc'
    },
    badge: {
      padding: isMobile ? '3px 8px' : '4px 10px', borderRadius: 4,
      fontSize: isMobile ? 10 : 11, fontWeight: 600, display: 'inline-block', letterSpacing: '0.3px'
    },
    badgePurchase: { background: '#edf2f7', color: '#2d3748' },
    actionBtn: {
      padding: isMobile ? '4px 8px' : '6px 12px', borderRadius: 6, border: '1px solid',
      fontSize: isMobile ? 11 : 12, fontWeight: 600, cursor: 'pointer',
      marginRight: 6, transition: 'all 0.2s'
    },
    editBtn: { background: '#e0f2fe', color: '#1880c2', borderColor: '#bae6fd' },
    deleteBtn: { background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca' },
    modal: {
      position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center',
      alignItems: 'center', zIndex: 9999, padding: isMobile ? 12 : 20
    },
    modalContent: {
      background: '#ffffff', borderRadius: isMobile ? 12 : 16,
      width: isMobile ? '100%' : isTablet ? '80%' : 'min(500px, 95%)',
      maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden'
    },
    modalHeader: {
      padding: isMobile ? 16 : 24, borderBottom: '1px solid #e1e8ed',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      background: '#fafbfc', flexShrink: 0
    },
    modalBody: { padding: isMobile ? 16 : 24, overflowY: 'auto', flex: 1 },
    modalFooter: {
      padding: isMobile ? 16 : 24, borderTop: '1px solid #e1e8ed',
      display: 'flex', flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'flex-end', gap: 12, background: '#fafbfc', flexShrink: 0
    },
    mobileCard: {
      background: '#fff', borderRadius: 10, border: '1px solid #e1e8ed',
      padding: 14, marginBottom: 12, cursor: 'pointer', transition: 'all 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
    },
    emptyState: { textAlign: 'center', padding: isMobile ? 30 : 60, color: '#718096' },
    capitalActions: { display: 'flex', gap: 6, position: 'absolute', top: 10, right: 10 },
    capitalBtn: {
      padding: '4px 6px', background: '#ffffff', color: '#4a5568',
      border: '1px solid #e1e8ed', borderRadius: 4, fontSize: 10,
      fontWeight: 600, cursor: 'pointer', display: 'flex',
      alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
    },
    badge2: {
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 12px', background: '#e0f2fe', color: '#1880c2',
      borderRadius: 20, fontSize: isMobile ? 11 : 12, fontWeight: 600,
      letterSpacing: '0.3px', flex: isSmallMobile ? 1 : 'none',
      justifyContent: 'center', width: isSmallMobile ? '100%' : 'auto'
    },
    filterContainer: {
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      width: '100%', flexDirection: isSmallMobile ? 'column' : 'row'
    }
  }), [isMobile, isTablet, isSmallMobile]);

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerTitle}>
          <div style={styles.headerIcon}>TX</div>
          <div>
            <div style={styles.headerText}>Transactions</div>
            <div style={styles.headerSubtext}>Financial transaction management</div>
          </div>
        </div>
        <div style={styles.headerActions}>
          <div style={styles.badge2}>
            {txStats.total} Transactions
          </div>
          <button
            onClick={printContent}
            style={styles.btnSuccess}
            onMouseEnter={(e) => e.currentTarget.style.background = '#15803d'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#16a34a'}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6L9 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 2v4h4M10 9H6M10 11H6M7 7H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {isMobile ? 'Export' : 'Export PDF'}
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            style={styles.btnPrimary}
            onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Transaction
          </button>
        </div>
      </header>

      <main style={styles.main}>
        {/* Stats Row */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: '#e0f2fe', color: '#1880c2' }}>CAP</div>
            <div style={styles.statContent}>
              <div style={styles.statNumber}>{fmt(summary.totalCapital)}</div>
              <div style={styles.statTitle}>Capital</div>
            </div>
            <div style={styles.capitalActions}>
              <button
                onClick={openCapitalModal}
                style={styles.capitalBtn}
                title="Edit Capital"
              >
                <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M12.146 1.854a.5.5 0 0 1 0 .707L11 3.707 8.293 1l1.146-1.146a.5.5 0 0 1 .708 0l2 2zM7.5 1.793L.646 8.646a.5.5 0 0 0-.146.354v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .354-.146L10.207 4.5 7.5 1.793z" />
                </svg>
              </button>
              <button
                onClick={() => setShowHistoryModal(true)}
                style={styles.capitalBtn}
                title="Capital History"
              >
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2zM8 4v4l2.5 1.5" />
                </svg>
              </button>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: '#fef5f5', color: '#991b1b' }}>PUR</div>
            <div style={styles.statContent}>
              <div style={{ ...styles.statNumber, color: '#dc2626' }}>{fmt(summary.totalPurchases)}</div>
              <div style={styles.statTitle}>Purchases</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: '#f0fdf4', color: '#166534' }}>PAY</div>
            <div style={styles.statContent}>
              <div style={{ ...styles.statNumber, color: '#16a34a' }}>{fmt(summary.totalPayments)}</div>
              <div style={styles.statTitle}>Payments</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: '#fef5f1', color: '#9a3412' }}>RET</div>
            <div style={styles.statContent}>
              <div style={{ ...styles.statNumber, color: '#c05621' }}>{fmt(summary.totalReturns)}</div>
              <div style={styles.statTitle}>Returns</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: '#fef5f5', color: '#991b1b' }}>DUE</div>
            <div style={styles.statContent}>
              <div style={{ ...styles.statNumber, color: '#dc2626' }}>{fmt(summary.payable)}</div>
              <div style={styles.statTitle}>Payable</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: summary.remainingCapital >= 0 ? '#f0fdf4' : '#fef5f5', color: summary.remainingCapital >= 0 ? '#166534' : '#991b1b' }}>
              BAL
            </div>
            <div style={styles.statContent}>
              <div style={{ ...styles.statNumber, color: summary.remainingCapital >= 0 ? '#16a34a' : '#dc2626' }}>
                {fmt(summary.remainingCapital)}
              </div>
              <div style={styles.statTitle}>Remaining</div>
            </div>
          </div>
        </div>

        {/* Search & Transactions */}
        <div style={styles.card} ref={printRef}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              Transactions <span style={{fontSize: 12, color: '#718096', fontWeight: 500}}>({filteredTxs.length})</span>
            </h3>
            
            <div style={styles.filterContainer}>
              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                style={{ ...styles.select, width: isSmallMobile ? '100%' : 'auto', padding: '6px 10px', fontSize: 12 }}
                className="no-print"
              >
                <option value="ALL">All Types</option>
                <option value="purchase">Procurement Pur.</option>
                <option value="payment">Vendor Payment</option>
                <option value="sale">Credit Sale</option>
                <option value="receipt">Cash Receipt</option>
                <option value="return">Return</option>
              </select>

              {/* Date Filter */}
              <select
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                style={{ ...styles.select, width: isSmallMobile ? '100%' : 'auto', padding: '6px 10px', fontSize: 12 }}
                className="no-print"
              >
                <option value="ALL">All Time</option>
                <option value="TODAY">Today</option>
                <option value="THIS_WEEK">This Week</option>
                <option value="THIS_MONTH">This Month</option>
                <option value="CUSTOM">Custom Dates</option>
              </select>

              {dateFilter === 'CUSTOM' && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', width: isSmallMobile ? '100%' : 'auto' }} className="no-print">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={e => setCustomStartDate(e.target.value)}
                    style={{ ...styles.input, flex: 1, padding: '6px 8px', fontSize: 12 }}
                  />
                  <span style={{ fontSize: 11, color: '#718096' }}>to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={e => setCustomEndDate(e.target.value)}
                    style={{ ...styles.input, flex: 1, padding: '6px 8px', fontSize: 12 }}
                  />
                </div>
              )}

              {/* Text Search Input */}
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search party..."
                style={{ ...styles.input, width: isSmallMobile ? '100%' : 140, padding: '6px 10px', fontSize: 12 }}
                className="no-print"
              />
            </div>
          </div>

          {loading ? (
            <div style={styles.emptyState}>
              <div style={{ width: 40, height: 40, border: '3px solid #e0f2fe', borderTopColor: '#1880c2', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              <div style={{ fontSize: 13, fontWeight: 500 }}>Loading transactions...</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : filteredTxs.length === 0 ? (
            <div style={styles.emptyState}>
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 12px', opacity: 0.2 }}>
                <path d="M9 2H4a1 1 0 00-1 1v18a1 1 0 001 1h16a1 1 0 001-1V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 2v6h6M12 11v6M9 14h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No matching transactions</div>
              <div style={{ fontSize: 12 }}>Try adjusting your filters or search query</div>
            </div>
          ) : !isMobile ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 40 }}>#</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Party</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Invoice</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                    <th style={{ ...styles.th, textAlign: 'center', width: 130 }} className="no-print">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxs.map((t, i) => (
                    <tr
                      key={t._id}
                      style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                      onClick={() => setSelectedTx(t)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={styles.td}>{i + 1}</td>
                      <td style={styles.td}>
                        <div style={{ fontSize: 13 }}>{new Date(t.date).toLocaleDateString('en-IN')}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>
                          {new Date(t.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td style={{ ...styles.td, fontWeight: 600 }}>{t.party?.name || 'Unknown'}</td>
                      <td style={styles.td}>
                        <TypeBadge type={t.type} isMobile={isMobile} baseStyle={styles.badge} purchaseStyle={styles.badgePurchase} />
                      </td>
                      <td style={styles.td}>{t.invoiceNumber || '—'}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        <AmountDisplay value={t.amount} type={t.type} isMobile={isMobile} />
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }} className="no-print" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => openEdit(t)}
                          style={{ ...styles.actionBtn, ...styles.editBtn }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteTx(t._id)}
                          style={{ ...styles.actionBtn, ...styles.deleteBtn, marginRight: 0 }}
                        >
                          Del
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 12 }}>
              {filteredTxs.map((t) => (
                <MobileTxCard key={t._id} tx={t} isMobile={isMobile} styles={styles} onSelect={setSelectedTx} />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Transaction Modal */}
      {showAddForm && (
        <div style={styles.modal} onClick={() => setShowAddForm(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1a202c' }}>
                Add Transaction
              </h2>
            </div>

            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={styles.modalBody}>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Party *</label>
                    <select
                      value={party}
                      onChange={(e) => setParty(e.target.value)}
                      required
                      style={styles.select}
                    >
                      <option value="">Select party</option>
                      {parties.map((p) => (
                        <option key={p._id} value={p._id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Type</label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      style={styles.select}
                    >
                      <option value="purchase">Procurement Purchase</option>
                      <option value="payment">Vendor Payment</option>
                      <option value="sale">Credit Sale</option>
                      <option value="receipt">Cash Receipt</option>
                      <option value="return">Return</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                      style={styles.input}
                      placeholder="0.00"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Invoice Number</label>
                    <input
                      type="text"
                      value={invoice}
                      onChange={(e) => setInvoice(e.target.value)}
                      style={styles.input}
                      placeholder="INV-001"
                    />
                  </div>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  style={{ ...styles.btnSecondary, background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.btnPrimary}
                >
                  Add Transaction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Capital Edit Modal */}
      {showCapitalModal && (
        <div style={styles.modal} onClick={() => setShowCapitalModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1a202c' }}>
                Update Capital
              </h2>
            </div>

            <form onSubmit={updateCapital} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={styles.modalBody}>
                <div style={{ ...styles.formGroup, marginBottom: 16 }}>
                  <label style={styles.label}>New Capital Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newCapital}
                    onChange={(e) => setNewCapital(e.target.value)}
                    style={styles.input}
                    placeholder="Enter new capital amount"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Admin Password *</label>
                  <input
                    type="password"
                    value={capitalPassword}
                    onChange={(e) => setCapitalPassword(e.target.value)}
                    style={styles.input}
                    placeholder="Enter admin password"
                    required
                  />
                  <div style={{
                    fontSize: 11,
                    color: '#718096',
                    marginTop: 8,
                    padding: '8px 12px',
                    background: '#fef3c7',
                    borderRadius: 6,
                    border: '1px solid #fde68a'
                  }}>
                    Default password: admin123
                  </div>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button
                  type="button"
                  onClick={() => setShowCapitalModal(false)}
                  style={{ ...styles.btnSecondary, background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca' }}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.btnPrimary}>
                  Update Capital
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Capital History Modal */}
      {showHistoryModal && (
        <div style={styles.modal} onClick={() => setShowHistoryModal(false)}>
          <div style={{ ...styles.modalContent, width: isMobile ? '100%' : isTablet ? '90%' : 'min(700px, 95%)' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1a202c' }}>
                Capital History
              </h2>
            </div>

            <div style={styles.modalBody}>
              {capitalHistory.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={{ fontSize: 14 }}>No capital changes recorded yet</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e1e8ed', borderRadius: 8 }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Date</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Previous</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Change</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {capitalHistory.map((entry) => (
                        <tr key={entry.id}>
                          <td style={styles.td}>
                            {new Date(entry.date).toLocaleString('en-IN', {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right' }}>Rs {fmt(entry.oldAmount)}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <span style={{
                              ...styles.badge,
                              ...(entry.change >= 0 ? { background: '#f0fdf4', color: '#166534' } : { background: '#fef5f5', color: '#991b1b' })
                            }}>
                              {entry.change >= 0 ? '↑' : '↓'} Rs {fmt(Math.abs(entry.change))}
                            </span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>Rs {fmt(entry.newAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button onClick={() => setShowHistoryModal(false)} style={styles.btnPrimary}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      {editOpen && (
        <div style={styles.modal} onClick={() => setEditOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1a202c' }}>
                Edit Transaction
              </h2>
            </div>

            <form onSubmit={updateTx} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={styles.modalBody}>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Party</label>
                    <select
                      value={editParty}
                      onChange={(e) => setEditParty(e.target.value)}
                      required
                      style={styles.select}
                    >
                      <option value="">Select party</option>
                      {parties.map((p) => (
                        <option key={p._id} value={p._id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Type</label>
                    <select
                      value={editType}
                      onChange={(e) => setEditType(e.target.value)}
                      style={styles.select}
                    >
                      <option value="purchase">Procurement Purchase</option>
                      <option value="payment">Vendor Payment</option>
                      <option value="sale">Credit Sale</option>
                      <option value="receipt">Cash Receipt</option>
                      <option value="return">Return</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)}
                      required
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Invoice Number</label>
                    <input
                      type="text"
                      value={editInvoice}
                      onChange={(e) => setEditInvoice(e.target.value)}
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button
                  type="button"
                  onClick={() => setEditOpen(false)}
                  style={{ ...styles.btnSecondary, background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca' }}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.btnPrimary}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal (Mobile) */}
      {selectedTx && (
        <div style={styles.modal} onClick={() => setSelectedTx(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: isMobile ? 16 : 24, borderBottom: '1px solid #e1e8ed', background: '#fafbfc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1a202c' }}>
                    {selectedTx.party?.name || 'Unknown'}
                  </h2>
                  <div style={{ marginTop: 8 }}>
                    <TypeBadge type={selectedTx.type} isMobile={isMobile} baseStyle={styles.badge} purchaseStyle={styles.badgePurchase} />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: isMobile ? 16 : 24, textAlign: 'center', background: '#f8fafc', borderBottom: '1px solid #e1e8ed' }}>
              <div style={{ fontSize: 11, color: '#718096', textTransform: 'uppercase', marginBottom: 4, fontWeight: 600, letterSpacing: '0.5px' }}>Amount</div>
              <div style={{
                fontSize: isMobile ? 24 : 32,
                fontWeight: 800,
                color: selectedTx.type === 'purchase' || selectedTx.type === 'sale' ? '#dc2626' : selectedTx.type === 'payment' || selectedTx.type === 'receipt' ? '#16a34a' : '#c05621'
              }}>
                Rs {fmt(selectedTx.amount)}
              </div>
            </div>

            <div style={{ padding: isMobile ? 16 : 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#718096', fontSize: 13, fontWeight: 500 }}>Invoice Number</span>
                <span style={{ fontWeight: 600, color: '#1a202c', fontSize: 13 }}>{selectedTx.invoiceNumber || 'N/A'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#718096', fontSize: 13, fontWeight: 500 }}>Date</span>
                <span style={{ fontWeight: 600, color: '#1a202c', fontSize: 13 }}>
                  {new Date(selectedTx.date).toLocaleDateString('en-IN', {
                    weekday: 'long', day: 'numeric', month: 'short', year: 'numeric'
                  })}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#718096', fontSize: 13, fontWeight: 500 }}>Time</span>
                <span style={{ fontWeight: 600, color: '#1a202c', fontSize: 13 }}>
                  {new Date(selectedTx.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
                <span style={{ color: '#718096', fontSize: 13, fontWeight: 500 }}>Tx ID</span>
                <span style={{ fontWeight: 600, color: '#1a202c', fontSize: 10, fontFamily: 'monospace' }}>
                  {selectedTx._id}
                </span>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={() => openEdit(selectedTx)}
                style={styles.btnPrimary}
              >
                Edit
              </button>
              <button
                onClick={() => deleteTx(selectedTx._id)}
                style={{ ...styles.btnSecondary, background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}